import React from 'react'
import { withRouter, RouteComponentProps } from 'react-router'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { Role, User } from 'types'
import { updateUser } from '../components/Page/auth/actions'
import { getCurrentUser, getUserPermission } from 'http/auth'
import { DEFAULT_ZONE, DEFAULT_COUNTRY } from 'utils/constants'

interface State extends User {
  user: any
  initialised: boolean
}

interface Props extends RouteComponentProps {
  updateUser: (user: User, role: Role, menu: any) => void
}

const ROLES = {
  [Role.UNAUTHENTICATED]: {
    accessiblePaths: [],
    menu: [],
  },
  [Role.ADMIN]: {
    accessiblePaths: ['/', '/register', '/search', '/tank', '/settings'],
    menu: [
      {
        link: '/register',
        name: 'Register',
      },
      {
        link: '/search',
        name: 'Search',
      },
    ],
  },
  [Role.CUSTOMER]: {
    accessiblePaths: ['/', '/tank', '/settings',],
    menu: []
  },
  [Role.FITTER]: {
    accessiblePaths: ['/register', '/search'],
    menu: [
      {
        link: '/register',
        name: 'Register',
      },
      {
        link: '/search',
        name: 'Search',
      },
    ],
  },
  [Role.CLT]: {
    accessiblePaths: ['/', '/register', '/search', '/tank', '/settings'],
    menu: [
      {
        link: '/register',
        name: 'Register',
      },
      {
        link: '/search',
        name: 'Search',
      },
    ],
  },
}

const isAuthorised = (role: Role, pathname: string): boolean => {
  const isAccessible = ROLES[role].accessiblePaths.find((path: string): boolean => {
    if (pathname.startsWith('/tank/')) {
      return path === '/tank'
    }
    else if (pathname.startsWith('/settings/')) {
      return path === '/settings'
    }
    return pathname === path
  })
  return isAccessible
}

const authenticator = (Component: any) => {
  class _Authenticator extends React.Component<Props, State> {
    public constructor(props) {
      super(props)
      this.state = {
        initialised: false,
        user: null,
        role: Role.UNAUTHENTICATED,
        zone: DEFAULT_ZONE,
        country: DEFAULT_COUNTRY,
        userName: '',
        customerId: '',
        countryFilter: null,
        stateFilter: null
      }
    }

    public async componentDidMount() {
      const {
        history,
        location: { pathname },
      } = this.props

      try {
        let role = Role.UNAUTHENTICATED
        const user = await getCurrentUser()
        const permission = await getUserPermission()

        if (!permission) {
          if (pathname !== '/403') {
            history.push('/403')
          }
        } else {
          // at this stage, the user is an authenticated IOTMon user
          if (pathname === '/auth') {
            // should not access the auth path
            return history.push('/')
          }

          if (permission.r === 'a') {
            role = Role.ADMIN
          } else if (permission.r === 'f') {
            role = Role.FITTER
          } else if (permission.r === 'c') {
            role = Role.CUSTOMER
          } else if (permission.r === 'clt') {
            role = Role.CLT
          }

          if (!isAuthorised(role, pathname)) {
            if (role === Role.FITTER && pathname === '/') {
              history.push('/register')
            } else {
              history.push('/403')
            }
          }

          this.setState(state => ({
            ...state,
            user,
            role,
            country: permission.z,
            zone: permission.z,
            customerId: permission.rv,
            countryFilter: permission.cntr,
            stateFilter: permission.states
          }))
          this.props.updateUser(user, role, ROLES[role].menu)
        }
      } catch (error) {
        console.log(error)
        // unauthenticated
        history.push('/auth')
      }

      this.setState(state => ({
        ...state,
        initialised: true,
      }))
    }

    public render() {
      const { role, country, zone, userName, customerId, initialised, countryFilter, stateFilter } = this.state
      const user = {
        country,
        zone,
        role,
        userName,
        customerId,
        countryFilter,
        stateFilter
      }
      if (!initialised) {
        return null
      }

      return <Component user={user} initialised={initialised} {...this.props} />
    }
  }

  const mapDispatchToProps = dispatch =>
    bindActionCreators(
      {
        updateUser,
      },
      dispatch,
    )

  return withRouter(
    connect(
      null,
      mapDispatchToProps,
    )(_Authenticator),
  )
}

export default authenticator
